// src/services/looker/lookerChatContext.ts
/**
 * What the assistant is told about the /looker page: the payload the tiles
 * were drawn from, capped. Built by the page, never by the bubble, so what the
 * model reads and what the user sees cannot drift (same rule as insights).
 *
 * No silent caps: each tile states how many rows it left out, whether Looker
 * had more than the page loaded, and its status.
 *
 * @module services/looker/lookerChatContext
 */

import { isHiddenWhenEmpty, type AppTileState } from '@/hooks/useLookerAppTiles';
import type { BackfillState } from '@/hooks/useLookerBackfill';
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { formatCurrency } from '@/config/brand';
import { formatConversions } from '@/utils/format';
import { OUTCOME_FIGURES, type AppTileData, type BackfillCounts, type FreshChannel } from './lookerAppData';
import { budgetPacingChatOf, type BudgetPacingChat } from './budgetPacingData';
import type { TileResultMetric, LookerDashboardPayload, LookerTile, LookerTileType, TileTargetCell, TileTargets } from './lookerTypes';
import type { CoverageNotesInput } from './lookerCoverageLabels';
import { badgeCoverageOf, coverageLinesOf, NO_TILE_NOTES, rowNotesByName, tileNotesOf, type TileNotes } from './lookerTileNotes';

/** Rows per tile that reach the prompt. The context is re-sent every turn. */
export const LOOKER_CHAT_ROW_CAP = 20;

export interface LookerChatTile {
  title: string;
  type: LookerTileType;
  status: 'ok' | 'empty' | 'unsupported' | 'failed';
  columns: string[];
  rows: Array<Record<string, string | null>>;
  rowsOmitted: number;
  /** There were more rows than the page loaded (/looker: Looker's 500-row cap; D1 and D2 always send false). */
  moreRowsThanLoaded: boolean;
  /** The tile's own totals over every matching row, by column label (/looker: Looker's; D2: its "รวม" row). Absent = none, as on D1. */
  totals?: Record<string, string | null>;
  /** Single value: the previous period's rendered values by column label, so the chat states the same Δ as the page. */
  previous?: Record<string, string | null>;
  /** Result columns are counted through this day (the last 3 days are cut). Set only where the page shows the same note. */
  resultThrough?: string;
  /** The period the Result Δ is compared with (it can differ from `period`). Set only where the page states it. */
  resultPrevious?: { start: string; end: string };
  /** App box 7 only: its rows cover the last N days, not the page's period, so never measure them against the period line. */
  windowDays?: number;
  /** App box 7 only: the ranking was computed on part of the set. */
  rankingPartial?: boolean;
  /** App box 6 only: this month against the approved plan, or { status: 'noPlan', reason }. */
  budgetPacing?: BudgetPacingChat;
  /** The card's coverage notes, as the page shows them (e.g. "google ไม่ส่ง Conversions"). Only when non-empty. */
  coverageNotes?: string[];
  /** Row name (its dimension values) → chips, for rows sent here only. Only when non-empty. */
  rowNotes?: Record<string, string[]>;
  /** DEC5 (R8): Cost per Result against the approved target, one entry per objective. Absent = not judged. */
  targets?: LookerChatTarget[];
  /** The day the targets were looked up on (resultThrough, else the period end). */
  targetsAsOf?: string;
  /**
   * DEC3: how this tile's Awareness Result is counted (impressions or reach),
   * whether the workspace chose it, a failed read, or the central definition,
   * and where Awareness shows (rows / card). Only on a tile with a Result field.
   */
  resultMetric?: TileResultMetric;
  /** The currency the targets are in; null = no single, certain currency, so every status is noCurrency. */
  targetCurrency?: string | null;
}

/** One objective's entry; targetPlatform = the channel whose own target was used, null = the objective-level one. */
export interface LookerChatTarget extends Omit<TileTargetCell, 'platform'> {
  targetPlatform: TileTargetCell['platform'];
}

export const targetFields = (targets: TileTargets | undefined): Pick<LookerChatTile, 'targets' | 'targetsAsOf' | 'targetCurrency'> =>
  targets
    ? {
      targets: Object.values(targets.byObjective).flatMap((c): LookerChatTarget[] => {
        if (!c) return [];
        const { platform, ...rest } = c;
        return [{ ...rest, targetPlatform: platform }];
      }),
      targetsAsOf: targets.asOf,
      targetCurrency: targets.currency,
    }
    : {};

/** Chunk counts of one platform or account, as the chat reads them. */
interface LookerChatBackfillCounts {
  window: { from: string | null; to: string | null };
  done: number;
  waiting: number;
  running: number;
  stuck: number;
  failed: number;
}

/** One ad account of a platform's open history download. */
export interface LookerChatBackfillAccount extends LookerChatBackfillCounts {
  /** null = jobs that carry no account (the page's "unassigned" row). */
  accountId: string | null;
  name: string | null;
  /** The newest gap-free run of done days; it may end before the window end. Both null = nothing done yet. */
  availableFrom: string | null;
  availableTo: string | null;
}

/** One platform of the open history download, as the chat reads it. */
export interface LookerChatBackfillRow extends LookerChatBackfillCounts {
  platform: string;
  accounts: LookerChatBackfillAccount[];
}

/** The open history download: per channel, plus one lower-bound ETA for the whole window. */
export interface LookerChatBackfill {
  channels: LookerChatBackfillRow[];
  /** null = cannot estimate yet (never 0). */
  etaMinutes: number | null;
  /** true = other workspaces' queued jobs could not be counted, so etaMinutes is a floor; false = they were counted. */
  etaIsLowerBound: boolean;
  // No queueAhead: the other workspaces' job count never leaves the ETA (owner ruling 2026-10-13).
}

/** One ad channel's sync, as the chat reads it. */
export interface LookerChatFreshnessRow {
  platform: string;
  /** stale = newest data more than 3 days behind today (Home's rule); never_synced and sync_failed are not fresh either. */
  state: 'fresh' | 'stale' | 'never_synced' | 'sync_failed';
  lastSyncedAt: string | null;
  dataThrough: string | null;
  /** Days the judged date lags today (Bangkok); null = never synced. */
  ageDays: number | null;
  /** 'data' = counted from dataThrough; 'sync' = the channel holds no data, so counted from the last sync. */
  ageSource: 'data' | 'sync' | null;
}

export interface LookerChatContext {
  kind: 'looker';
  template: { id: string; title: string };
  scope: { allWorkspaces: boolean; workspaceName: string | null };
  filters: Array<{ title: string; value: string }>;
  /** The bounded range the date filter resolved to and the equal period before it. null = none. */
  period?: { start: string; end: string; previousStart: string; previousEnd: string } | null;
  latestDataDate: string | null;
  /**
   * FR-X-02: every money number is in this currency (the server forced it
   * because the scope has several). null = one currency, nothing forced.
   */
  currency: string | null;
  /** Present only when true: accounts of unknown currency are not in any number. */
  currencyUnknownExcluded?: true;
  tiles: LookerChatTile[];
  /**
   * Page level, not a tile: history still being fetched. Rows = open;
   * null = none open; {status:'failed'} = could not be read. Absent = not
   * known (loading, or the all-workspaces view).
   */
  backfill?: LookerChatBackfill | null | { status: 'failed' };
  /**
   * Page level, not a tile: each ad channel's sync. Rows = ad channels;
   * null = none connected; {status:'failed'} = could not be read. Absent = not
   * known (loading, or the all-workspaces view).
   */
  channelFreshness?: LookerChatFreshnessRow[] | null | { status: 'failed' };
}

/**
 * One label per field; a repeated label gets the field name appended to each
 * repeat. Shared with the CSV (lookerCsv), so its headers are the chat's names.
 */
export const uniqueLabels = (fields: Array<{ name: string; label: string; role?: string }>, shareLabel: string): string[] => {
  const named = fields.map((f) => ({ ...f, label: f.role === 'share' ? shareLabel : f.label }));
  return uniqueNamed(named);
};

const uniqueNamed = (fields: Array<{ name: string; label: string }>): string[] => {
  const seen = new Map<string, number>();
  fields.forEach((f) => seen.set(f.label, (seen.get(f.label) ?? 0) + 1));
  return fields.map((f) => ((seen.get(f.label) ?? 0) > 1 ? `${f.label} (${f.name})` : f.label));
};

/** An app box as the chat sees it; null = not told (loading, needs a workspace, hidden when empty). */
type ChatRow = Record<string, string | null>;

/**
 * Box 8's coverage lines as rows of its own table: the platform in the first column, the line in the second, and,
 * from a ready read, `windowCell` (the selected period, by column label) in the window column. A failed or idle read
 * is a status line with no period read behind it, so its window stays null.
 */
const coverageRows = (columns: readonly string[], coverage: CoverageNotesInput | undefined, windowCell: Record<string, string> = {}): ChatRow[] => {
  if (!coverage || columns.length < 2) return [];
  const cell = coverage.state.status === 'ready' ? windowCell : {};
  return coverageLinesOf(coverage.state, coverage.labels).map((l) => ({
    ...Object.fromEntries(columns.map((c) => [c, null])),
    ...cell,
    [columns[0]]: l.platform,
    [columns[1]]: l.text,
  }));
};

/** Each box 10 figure as OutcomesBox prints it: counts grouped, money in its currency (none known: no marker). */
const amount = (n: number, currency: string | null): string =>
  (currency === null ? formatCurrency(n, false) : formatCurrency(n, true, currency));
const OUTCOME_TEXT: Record<(typeof OUTCOME_FIGURES)[number], (n: number, currency: string | null) => string> = {
  days: (n) => String(n),
  leads: formatConversions, bookings: formatConversions, sales: formatConversions,
  revenue: amount, spend: amount, costPerLead: amount,
  mediaRoas: (n) => `${n.toFixed(2)}×`,
};

/**
 * Box 10's rows in the box's words, so the chat quotes ฿902,530.11 where the page does, never 902530.11; a null stays
 * null where the box prints "—". The CSV keeps the raw cells. Split: the first row's revenue has no marker, as on the
 * box, and each later row's spend is in that row's own currency.
 */
const outcomeRows = ({ table, money }: Extract<AppTileData, { widget: 'outcomes' }>): ChatRow[] => {
  // tableOf writes `values` in OUTCOME_FIGURES order: each figure's label, wherever its column sits.
  const figureOf = new Map(OUTCOME_FIGURES.map((f, i) => [table.values?.[i], f]));
  return table.rows.map((row, i) => {
    // Split: the first row holds the counts and no spend; row i is the spend in spends[i - 1]'s currency.
    const currency = money.kind === 'one' ? money.currency : i === 0 ? null : money.spends[i - 1].currency;
    return Object.fromEntries(Object.entries(row).map(([column, cell]) => {
      const figure = figureOf.get(column);
      return [column, figure === undefined || cell === null ? cell : OUTCOME_TEXT[figure](Number(cell), currency)];
    }));
  });
};

export const appChatTile = (t: Pick<LookerTile, 'title' | 'type'>, state: AppTileState | undefined, coverage: CoverageNotesInput | undefined): LookerChatTile | null => {
  const base = { title: t.title, type: t.type, rowsOmitted: 0, moreRowsThanLoaded: false };
  if (state?.status === 'failed') return { ...base, status: 'failed', columns: [], rows: [] };
  if (state?.status !== 'ready' || isHiddenWhenEmpty(state)) return null;
  const { data } = state;
  const rows = data.widget === 'outcomes' ? outcomeRows(data) : data.table.rows;
  const all = [...rows, ...(data.widget === 'dataQuality' ? coverageRows(data.table.columns, coverage, data.coverageWindow) : [])];
  const shown = all.slice(0, LOOKER_CHAT_ROW_CAP);
  return {
    ...base,
    status: shown.length === 0 ? 'empty' : 'ok',
    columns: data.table.columns,
    rows: shown,
    rowsOmitted: all.length - shown.length,
    ...(data.widget === 'attention' ? { windowDays: data.windowDays, rankingPartial: data.truncated } : {}),
    ...(data.widget === 'budgetPacing' ? { budgetPacing: budgetPacingChatOf(data.view) } : {}),
    // The box's footnote, which says which days its figures count, as the tile's note (chat test 2026-10-22).
    ...(data.widget === 'outcomes' && data.daysNote ? { coverageNotes: [data.daysNote] } : {}),
  };
};

const chatCounts = (c: BackfillCounts): LookerChatBackfillCounts => ({
  window: { from: c.from, to: c.to },
  done: c.done,
  waiting: c.queued,
  running: c.running,
  stuck: c.stuck,
  failed: c.failed,
});

export const backfillOfState = (state: BackfillState): Pick<LookerChatContext, 'backfill'> => {
  if (state.status === 'failed') return { backfill: { status: 'failed' } };
  if (state.status !== 'ready') return {};
  if (state.platforms.length === 0) return { backfill: null };
  return {
    backfill: {
      channels: state.platforms.map((p) => ({
        platform: p.platform,
        ...chatCounts(p),
        accounts: p.accounts.map((a) => ({ accountId: a.accountId, name: a.name, ...chatCounts(a), availableFrom: a.availableFrom, availableTo: a.availableTo })),
      })),
      etaMinutes: state.eta.minutes,
      etaIsLowerBound: state.eta.lowerBound,
    },
  };
};

const IDLE: BackfillState = { status: 'idle' };

const CHAT_STATE: Record<FreshChannel['state'], LookerChatFreshnessRow['state']> = {
  ok: 'fresh', stale: 'stale', never: 'never_synced', error: 'sync_failed',
};

export const freshnessOfState = (state: ChannelFreshnessState): Pick<LookerChatContext, 'channelFreshness'> => {
  if (state.status === 'failed') return { channelFreshness: { status: 'failed' } };
  if (state.status !== 'ready') return {};
  if (state.channels.length === 0) return { channelFreshness: null };
  return {
    channelFreshness: state.channels.map((c) => ({
      platform: c.platform, state: CHAT_STATE[c.state], lastSyncedAt: c.lastSyncedAt, dataThrough: c.dataThrough,
      ageDays: c.ageDays, ageSource: c.ageSource,
    })),
  };
};

const SYNC_IDLE: ChannelFreshnessState = { status: 'idle' };

const notesOf = (t: LookerTile, payload: LookerDashboardPayload, coverage: CoverageNotesInput | undefined): TileNotes =>
  coverage
    ? tileNotesOf(t, badgeCoverageOf(coverage.state, coverage.channels), payload.objective ?? null, coverage.labels)
    : NO_TILE_NOTES;

/** The page's notes for one tile, only where there are any; rows only among those sent. */
const notesFields = (t: LookerTile, shown: LookerTile['rows'], notes: TileNotes): Pick<LookerChatTile, 'coverageNotes' | 'rowNotes'> => {
  const rows = rowNotesByName(t, shown, notes);
  return {
    ...(notes.card.length > 0 ? { coverageNotes: notes.card } : {}),
    ...(rows.length > 0 ? { rowNotes: Object.fromEntries(rows) } : {}),
  };
};

export const buildLookerChatContext = (
  payload: LookerDashboardPayload,
  workspaceName: string | null,
  /** The viewer's language for the share column, whose label comes from the server in English. */
  shareLabel = 'Share',
  /** Each app box's state by tile id (useLookerAppTiles). Absent = no app box is told. */
  appStates: Readonly<Record<string, AppTileState>> = {},
  /** The page-level backfill (useLookerBackfill). Absent = not told. */
  backfill: BackfillState = IDLE,
  /** The page-level channel sync (useLookerChannelFreshness). Absent = not told. */
  freshness: ChannelFreshnessState = SYNC_IDLE,
  /** The page's coverage (useLookerCoverage) and its sentences. Absent = no coverage notes. */
  coverage?: CoverageNotesInput,
): LookerChatContext => ({
  ...backfillOfState(backfill),
  ...freshnessOfState(freshness),
  kind: 'looker',
  template: { id: payload.template.id, title: payload.template.title },
  scope: payload.scope.kind === 'all'
    ? { allWorkspaces: true, workspaceName: null }
    : { allWorkspaces: false, workspaceName },
  filters: payload.template.filters.filter((f) => f.value !== '').map((f) => ({ title: f.title, value: f.value })),
  period: payload.period
    ? { start: payload.period.start, end: payload.period.end, previousStart: payload.period.previousStart, previousEnd: payload.period.previousEnd }
    : null,
  latestDataDate: payload.freshness.latestDataDate,
  currency: payload.currency,
  ...(payload.currency && payload.currencyUnknownExcluded ? { currencyUnknownExcluded: true as const } : {}),
  tiles: payload.tiles.flatMap((t): LookerChatTile[] => {
    if (t.type === 'app') {
      const app = appChatTile(t, appStates[t.id], coverage);
      return app ? [app] : [];
    }
    const shown = t.rows.slice(0, LOOKER_CHAT_ROW_CAP);
    const labels = uniqueLabels(t.fields, shareLabel);
    return [{
      title: t.title,
      type: t.type,
      status: t.error ?? (t.rows.length === 0 ? 'empty' : 'ok'),
      columns: labels,
      rows: shown.map((r) =>
        Object.fromEntries(
          t.fields.map((f, i) => {
            const c = r[f.name];
            return [labels[i], c?.rendered ?? (c?.value == null ? null : String(c.value))];
          }),
        ),
      ),
      rowsOmitted: t.rows.length - shown.length,
      moreRowsThanLoaded: t.truncated,
      ...notesFields(t, shown, notesOf(t, payload, coverage)),
      ...targetFields(t.targets),
      ...(t.resultMetric ? { resultMetric: t.resultMetric } : {}),
      ...(t.resultThrough && !t.error && t.rows.length > 0 ? { resultThrough: t.resultThrough } : {}),
      ...(t.resultThrough && t.resultPrevious && !t.error && t.rows.length > 0
        ? { resultPrevious: { start: t.resultPrevious.start, end: t.resultPrevious.end } }
        : {}),
      ...(t.previous
        ? {
          previous: Object.fromEntries(
            t.fields.map((f, i) => {
              const c = t.previous?.[f.name];
              return [labels[i], c?.rendered ?? (c?.value == null ? null : String(c.value))];
            }),
          ),
        }
        : {}),
      ...(t.totals
        ? {
          totals: Object.fromEntries(
            t.fields.flatMap((f, i) => {
              const c = t.totals?.[f.name];
              return c ? [[labels[i], c.rendered ?? (c.value == null ? null : String(c.value))]] : [];
            }),
          ),
        }
        : {}),
    }];
  }),
});
